import {
  eventToChord,
  isPrintableAscii,
  logicalEventKeyToken,
  matchesEvent,
  normalizeChord,
  parseChord,
  type KeyboardEventLike,
} from '../model/chord';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { defaultKeybindings, keybindingCommandIds } from '../model/defaultKeybindings';
import type { LayoutMapCache } from '../model/layoutMap';
import { platform } from '../model/platform';
import { findReservedChord, isScopedReservedException } from '../model/reservedChords';
import type {
  ChordModifier,
  KeybindingCommandId,
  KeyboardLayoutSnapshot,
  Keymap,
  Platform,
} from '../model/keybindingTypes';
import { getEffectiveKeymap, getKeybindingLayoutMap } from './effectiveKeymap';
import { cancelRecording, releaseRecorderHeldKey, routeKeydownToRecorder } from './recorderSession';
import { resolveFocus } from './focusPolicy';
import { runAppCommand, type RunAppCommandResult } from './runAppCommand';
import { resolveActiveTarget, type ResolvedKeybindingTarget } from './targetRegistry';

export {
  setRecorder,
  startRecording,
  stopRecording,
  type KeybindingRecorder,
} from './recorderSession';

type KeybindingWindow = {
  addEventListener: (
    type: string,
    listener: EventListener,
    options?: boolean | AddEventListenerOptions,
  ) => void;
  removeEventListener: (
    type: string,
    listener: EventListener,
    options?: boolean | EventListenerOptions,
  ) => void;
};

export type InstallKeybindingDispatcherOptions = {
  readonly windowObject?: KeybindingWindow;
  readonly layoutMap?: Pick<LayoutMapCache, 'getSnapshot' | 'start' | 'dispose'>;
  readonly targetPlatform?: Platform;
  readonly isDev?: boolean;
  readonly getKeymap?: () => Keymap;
  readonly getActiveTabId?: () => string | null;
  readonly getTarget?: (
    commandId: KeybindingCommandId,
    activeTabId: string | null,
  ) => ResolvedKeybindingTarget | null;
  readonly runCommand?: (
    commandId: KeybindingCommandId,
    options: { source: 'keyboard' },
  ) => RunAppCommandResult;
};

const installedWindows = new WeakMap<object, () => void>();
function matchingCommands(
  event: KeyboardEvent, layout: KeyboardLayoutSnapshot, keymap: Keymap,
  targetPlatform: Platform, isDev: boolean,
): KeybindingCommandId[] {
  if (!eventToChord(event, layout)) return [];
  const matches = keybindingCommandIds.filter((commandId) =>
    keymap[commandId].some((chord) =>
      matchesEvent(chord, event, targetPlatform, layout) &&
      !matchInterceptsReservedChord(commandId, chord, event, targetPlatform, isDev)
    )
  );
  return [
    ...matches.filter((commandId) => defaultKeybindings[commandId].scope !== 'global'),
    ...matches.filter((commandId) => defaultKeybindings[commandId].scope === 'global'),
  ];
}

function eventModifiers(event: KeyboardEventLike): ChordModifier[] {
  return [
    ...(event.ctrlKey ? ['Ctrl' as const] : []),
    ...(event.metaKey ? ['Cmd' as const] : []),
    ...(event.shiftKey ? ['Shift' as const] : []),
    ...(event.altKey ? ['Alt' as const] : []),
  ];
}

function matchInterceptsReservedChord(
  commandId: KeybindingCommandId, binding: string, event: KeyboardEventLike,
  targetPlatform: Platform, isDev: boolean,
): boolean {
  const logicalKey = logicalEventKeyToken(event.key);
  if (!logicalKey) return false;
  const logicalChord = normalizeChord({
    modifiers: eventModifiers(event), key: logicalKey, physical: false,
  });
  if (!logicalChord || !findReservedChord(logicalChord, targetPlatform, isDev)) return false;
  const matchedBinding = parseChord(binding);
  const isSameLogicalChord = matchedBinding && !matchedBinding.physical && (
    matchedBinding.key === logicalKey ||
    (event.location === 3 && isPrintableAscii(event.key) && matchedBinding.key === event.code)
  );
  if (isSameLogicalChord) return false;
  return !isScopedReservedException(commandId, logicalChord, targetPlatform, isDev);
}

export function installKeybindingDispatcher(
  options: InstallKeybindingDispatcherOptions = {},
): () => void {
  const windowObject = options.windowObject ?? (globalThis.window as unknown as KeybindingWindow);
  const existing = installedWindows.get(windowObject);
  if (existing) return existing;

  const layoutMap = options.layoutMap ?? getKeybindingLayoutMap();
  const targetPlatform = options.targetPlatform ?? platform();
  const isDev = options.isDev ?? import.meta.env.DEV;
  const getKeymap = options.getKeymap ?? getEffectiveKeymap;
  const getActiveTabId = options.getActiveTabId ?? (() => useWorkspaceStore.getState().activeTabId);
  const getTarget = options.getTarget ?? ((commandId, activeTabId) => {
    const scope = defaultKeybindings[commandId].scope;
    return resolveActiveTarget(scope, activeTabId);
  });
  const runCommand = options.runCommand ?? runAppCommand;

  const keydownListener: EventListener = (rawEvent) => {
    const event = rawEvent as KeyboardEvent;
    const layout = layoutMap.getSnapshot();
    if (routeKeydownToRecorder(event, layout)) return;

    const activeTabId = getActiveTabId();
    for (const commandId of matchingCommands(event, layout, getKeymap(), targetPlatform, isDev)) {
      const definition = defaultKeybindings[commandId];
      const target = getTarget(commandId, activeTabId);
      if (definition.scope !== 'global' && (!target || !target.hasHandler(commandId))) continue;
      if (!resolveFocus({
        commandId,
        policy: definition.focus,
        worksInTerminal: definition.worksInTerminal,
        resolvedModifiers: eventModifiers(event),
        targetPlatform,
        target,
      })) continue;
      if (!runCommand(commandId, { source: 'keyboard' }).handled) continue;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
  };
  const blurListener: EventListener = () => cancelRecording();
  const keyupListener: EventListener = (rawEvent) => releaseRecorderHeldKey(rawEvent as KeyboardEvent);

  layoutMap.start();
  windowObject.addEventListener('keydown', keydownListener, true);
  windowObject.addEventListener('keyup', keyupListener, true);
  windowObject.addEventListener('blur', blurListener);
  const uninstall = () => {
    windowObject.removeEventListener('keydown', keydownListener, true);
    windowObject.removeEventListener('keyup', keyupListener, true);
    windowObject.removeEventListener('blur', blurListener);
    layoutMap.dispose();
    installedWindows.delete(windowObject);
  };
  installedWindows.set(windowObject, uninstall);
  return uninstall;
}
